<template>
  <div v-if="subscriber" class="cta-dropdown-container" @click.stop>
    <button
      type="button"
      class="header-navigation-link header-navigation-link--manage-logged-in"
      :aria-expanded="isOpen"
      aria-haspopup="true"
      @click="toggleDropdown"
    >
      {{ subscriber.email_address[0].toUpperCase() }}
    </button>
    <div v-if="isOpen" class="cta-dropdown-menu">
      <div class="cta-dropdown-item cta-dropdown-item--signed-in">
        Signed in as
        <div class="cta-dropdown-item-email">
          {{ subscriber.email_address }}
        </div>
      </div>
      <a
        v-if="manageSubscriptionUrl"
        :href="manageSubscriptionUrl"
        class="cta-dropdown-item"
        @click="closeDropdown"
      >
        Manage your subscription
      </a>
      <a
        href="/portal?logout=1"
        class="cta-dropdown-item"
        @click="closeDropdown"
      >
        Log out
      </a>
    </div>
  </div>
  <div v-else-if="isAuthenticated">
    <button
      type="button"
      class="header-navigation-link header-navigation-link--subscribe"
      :disabled="subscribeState === 'pending'"
      @click="oneClickSubscribe"
    >
      <svg
        v-if="subscribeState === 'pending'"
        class="cta-spinner"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2Z"
          stroke="currentColor"
          stroke-width="2.5"
          opacity="0.25"
        />
        <path
          d="M12 2C6.47715 2 2 6.47715 2 12"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
        />
      </svg>
      <template v-else>
        {{ t("web.button.subscribe") }}
      </template>
    </button>
  </div>
  <div v-else>
    <button
      type="button"
      class="header-navigation-link header-navigation-link--subscribe subscribe-modal-trigger"
    >
      {{ t("web.button.subscribe") }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";

import URLS from "@/autogen/urls";
import type { Newsletter } from "@/types/newsletter";

import { createTranslator } from "./lib/utils";
import {
  type SubscriberFacingAuthentication,
  subscribe as subscribeApi,
} from "./SubscribeForm/lib";

const props = defineProps<{
  newsletter: Newsletter;
  subscriberFacingAuthentication: SubscriberFacingAuthentication;
}>();

const t = createTranslator(() => props.newsletter.locale);

const isOpen = ref(false);
const subscribeState = ref<"idle" | "pending" | "success" | "error">("idle");

const isAuthenticated = computed(() => {
  return (
    props.subscriberFacingAuthentication?.status !== "unauthenticated" &&
    !!props.subscriberFacingAuthentication?.email_address
  );
});

const oneClickSubscribe = async () => {
  if (subscribeState.value === "pending") return;
  subscribeState.value = "pending";
  const result = await subscribeApi(
    props.subscriberFacingAuthentication.email_address,
    {},
    props.newsletter,
    null,
    null
  );
  if ("error" in result) {
    subscribeState.value = "error";
  } else {
    subscribeState.value = "success";
    if (props.newsletter.subscription_redirect_url) {
      window.location.href = props.newsletter.subscription_redirect_url;
    } else {
      window.location.reload();
    }
  }
};

// Find the subscriber for this newsletter
const subscriber = computed(() => {
  return props.subscriberFacingAuthentication?.subscribers?.find(
    (subscriber) => subscriber.newsletter_id === props.newsletter.id
  );
});

// Check if portal feature is enabled (required for manage subscription)
const manageSubscriptionUrl = computed(() => {
  if (!props.newsletter.enabled_features.includes("portal")) {
    return null;
  }
  if (!subscriber.value) {
    return null;
  }
  return URLS["manage-subscription"](
    props.newsletter.username,
    subscriber.value.id
  );
});

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const closeDropdown = () => {
  isOpen.value = false;
};

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Element;
  if (!target.closest(".cta-dropdown-container")) {
    closeDropdown();
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style scoped lang="css">
.cta-dropdown-container {
  position: relative;
}

.header-navigation-link--manage-logged-in {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background-color: var(--color-background-card);
  border: 1px solid var(--color-background-offset);
  border-radius: calc(var(--space));
  font-size: var(--font-size-extra-small);
  font-weight: 500;
  color: var(--color-foreground);
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
}

.header-navigation-link--manage-logged-in:hover {
  background-color: var(--color-background-offset);
}

.cta-dropdown-menu {
  position: absolute;
  top: calc(100% + calc(var(--space) / 4));
  right: 0;
  background-color: var(--color-background-card);
  border: 1px solid var(--color-background-offset);
  border-radius: calc(var(--space) / 2);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -2px rgba(0, 0, 0, 0.1);
  min-width: 160px;
  z-index: 1000;
  overflow: hidden;
}

.cta-dropdown-item {
  display: block;
  padding: calc(var(--space) / 2) calc(var(--space) / 2 + 4px);
  color: var(--color-foreground);
  text-decoration: none;
  font-size: var(--font-size-small);
  transition: background-color 0.2s ease-in-out;
}

.cta-dropdown-item:hover {
  background-color: var(--color-background-offset);
}

.cta-dropdown-item:first-child {
  border-bottom: 1px solid var(--color-background-offset);
}

.cta-dropdown-item--signed-in {
  font-weight: 400;
  color: var(--color-foreground-offset-2);
  font-size: var(--font-size-extra-small);
}

.cta-dropdown-item-email {
  font-weight: 500;
  color: var(--color-foreground);
  font-size: var(--font-size-small);
}

@keyframes cta-spin {
  to {
    transform: rotate(360deg);
  }
}

.cta-spinner {
  width: 1em;
  height: 1em;
  animation: cta-spin 0.75s linear infinite;
}
</style>
